import React, { useRef } from "react"
import styled from "styled-components"
import { Link } from "gatsby"

import useTracking from "hooks/useTracking"
import useProductTracking from "hooks/useProductTracking"
import { minWidth, maxWidth, when, eq } from "utils"
import StarIcon from "images/star-filled-icon.svg"
import Icon from "icons"
import CloudinaryImage from "components/cloudinary-image"
import CroppedImage from "components/cropped-image"
import ArticleIcon from "images/article-icon.svg"
import { showStarsInsteadOfAsterisks } from "utils/showStarsInsteadOfAsterisks"
import { formatTitleWithSmallSuffix } from "utils/formatTitleWithSmallSuffix"

export const Wrapper = styled.div`
  margin-top: 4rem;

  ${maxWidth.hamburgerLarge`
    padding-left: ${({ isScrollable }) => (isScrollable ? "0" : "7.3rem")};
    padding-right: 0;
    margin-top: 2rem;
  `}

  ${maxWidth.tablet`
    margin-top: 2rem;
  `}

  ${maxWidth.mobile`
    padding: ${({ isScrollable }) => (isScrollable ? "0" : "0 0 2rem 2rem ")};
    margin-top: 1rem;
  `}
`

export const Items = styled.div`
  position: relative;
`

const ScrollWrapper = styled.span`
  ${minWidth.mobile`
    ${eq("itemType", "Product")`
      display: none;
    `}
  `}

  ${when("isScrollable")`
    ${minWidth.mobile`
      display: none;
      `}  
  `}
`

const ScrollLabel = styled.div`
  font-size: 1.6rem;
  line-height: 2.2rem;
  color: ${({ theme }) => theme.component?.text || theme.colors.text.primary};

  ${minWidth.hamburgerLarge`
    font-size: 2rem;
    line-height: 2.7rem;
  `}
`

const ScrollArrowLineMobile = styled(Icon)`
  color: ${({ theme }) => theme.component?.text || theme.colors.text.primary};
  margin: 0 1.5rem 1.5rem 0;
  ${minWidth.mobile`
    margin:0;
    display: none;
  `};
`

const ScrollArrowLineDesktop = styled(Icon)`
  display: none;
  color: ${({ theme }) => theme.component?.text || theme.colors.text.primary};

  ${minWidth.mobile`
    display: block;
  `}
`

export const Scroll = ({ itemType, label, isScrollable }) => (
  <ScrollWrapper itemType={itemType} isScrollable={isScrollable}>
    <ScrollLabel>{label || "scroll"}</ScrollLabel>
    <ScrollArrowLineMobile viewBox="0 0 348 12" id="arrow-line-mobile" />
    <ScrollArrowLineDesktop viewBox="0 0 823 12" id="arrow-line-desktop" />
  </ScrollWrapper>
)

export const ScrollContainer = styled.div`
  position: relative;
  width: 100%;
  height: auto;
`

export const Grid = styled.div`
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-behavior: smooth;
  gap: 3.6rem;

  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;

  @media (min-width: 1025px) {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    overflow: visible;
  }
`

const ArrowIcon = styled(Icon)`
  position: absolute;
  top: -4.8rem;
  right: 3.5rem;
  transform: translateY(-2.5rem) rotate(180deg);
  display: block;
  color: ${({ theme }) => theme.component?.text || theme.colors.text.primary};
  cursor: pointer;
  outline: none;

  ${when("disabled")`
    color: ${({ theme }) => (theme.component?.isDarkBackground ? "#aaa" : "rgba(34, 34, 34, 0.2)")};
    pointer-events: none;
  `}

  ${minWidth.hamburgerLarge`
    display: none;
  `}

  ${eq("direction", "right")`
    right: 0;
    transform: translateY(-2.5rem);
    transform-origin: center;
  `}

  ${when("ismodal")`
    top: 50%;
    left: 0;
    color: #fff;

    ${when("disabled")`
      color: #aaa;
    `}

    ${eq("direction", "right")`
      left: unset;
      right: 0;
    `}
  `}

  ${when("isScrollable")`
    ${minWidth.tablet`
      margin-top: -6rem;
      right: 3.5rem;
      top: 0;
      padding: 0;

      ${eq("direction", "right")`
        right: 0;
        transform: translateY(-2.5rem);
        transform-origin: center;
      `}
    `}
  `}
`

export const Arrow = (props) => <ArrowIcon id="arrow-shop" width={24} height={25} {...props} />

export const ProductCardWrapper = styled.div`
  display: flex;
`

const Card = styled.div`
  border-radius: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
`

const ArticleCardContainer = styled.div`
  border-radius: 0;
  display: flex;
  flex-direction: column;
  :first-child {
    margin-left: 0;
  }

  :last-child {
    margin-right: 0;
  }

  position: relative;
  margin: 0 0.9rem;
  background: #ffffff;
  width: 24.7rem;
  height: 34.9rem;

  ${minWidth.mobile`
    width: 25.3rem;
    height: 35.8rem;
  `}
`
const ArticleContent = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  flex: 1 0 auto;
  padding: 1.5rem;

  ${minWidth.mobile`
    padding: 2rem
  `}
`

const ProductImage = styled(CloudinaryImage)`
  padding-bottom: 100%;
  border-radius: 1rem;
  /* transition: transform 0.2s linear; */

  /* &:hover {
    transform: scale(1.25);
  } */
`

const ProductMeta = styled.div`
  padding: 2rem 1rem;
  display: flex;
  flex-direction: column;
  flex: 1 0 auto;
`

const ProductCategory = styled.h4`
  color: #2260b2;
  font-size: 1.2rem;
  font-weight: 500;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  margin-bottom: 0.9rem;
`

const ProductTitle = styled.h3`
  color: #12233f;
  font-size: 1.9rem;
  font-weight: 400;
  line-height: 2.6rem;
  min-height: 10rem;

  .text-small {
    display: none;

    ${minWidth.mobile`
      display: inline-block;
      color: #12233f;
      font-size: 1.5rem;
      font-weight: 400;
      line-height: 2rem;
      margin-top: 0.5rem;
    `}
  }

  .price {
    display: block;
    margin-top: 1rem;
  }
`

const ProductReviewByline = styled.div`
  display: none;

  ${minWidth.mobile`
    color: #000;
    display: flex;
    align-items: center;
    gap: 1rem;
    font-size: 1.2rem;
    font-weight: 400;
    letter-spacing: 0.024rem;
    text-transform: lowercase;
    margin-top: 0.5rem;
    
    & > span {
      display: flex;
      margin-bottom: 0.2rem;
    }
  `}
`

const Empty = styled.div`
  margin: 0.75rem 0;
  flex: 1 0 auto;
  font-size: 1.6rem;
  line-height: 2.1rem;
`

export const ProductCard = (cardItem) => {
  const {
    shopifyProductVariant,
    reviewByline,
    category,
    image: imageProp,
    headlineCard: headline,
  } = cardItem

  const { price, product } = shopifyProductVariant
  if (!product) {
    return null
  }

  const { image, media, onlineStoreUrl, productId, title } = product
  const mockCategory = category || "best sellers"

  const { trackProductClick } = useProductTracking()
  const ref1 = useRef()
  const ref2 = useRef()

  const item = {
    itemType: "Product",
    itemId: productId,
    shopifyProductVariant: { price, product: { title } },
  }
  trackProductClick({ elementRef: ref1, item })
  trackProductClick({ elementRef: ref2, item })

  const imagePath = imageProp?.[0]?.secure_url || image || media[0]?.image.originalSrc

  return (
    <Card>
      <ImageLink
        ref={ref1}
        href={onlineStoreUrl}
        title={title}
        target="_blank"
        rel="noreferrer"
        tabIndex={-1}
      >
        <ProductImage
          baseUrl={imagePath}
          images={[
            {
              screenSize: 768,
              transforms: "c_fill,g_auto,w_260,h_260,q_auto,f_auto,fl_lossy",
            },
            {
              transforms: "c_fill,g_auto,w_202,h_202,q_auto,f_auto,fl_lossy",
            },
          ]}
          lazy
        />
      </ImageLink>
      <ProductMeta>
        <a
          ref={ref2}
          href={onlineStoreUrl}
          title={headline || title}
          target="_blank"
          rel="noreferrer"
        >
          <ProductCategory>{mockCategory}</ProductCategory>
          <ProductTitle>
            {formatTitleWithSmallSuffix(headline || title)}
            <span className="price">${price}</span>
          </ProductTitle>

          {reviewByline && (
            <ProductReviewByline
              dangerouslySetInnerHTML={{
                __html: showStarsInsteadOfAsterisks(reviewByline, StarIcon),
              }}
            />
          )}
        </a>
        <Empty />
      </ProductMeta>
    </Card>
  )
}

export const ImageLink = styled.a`
  width: 20.2rem;

  @media (min-width: 1025px) {
    width: 100%;
    object-fit: cover;
    object-position: center;
  }
`

export const ImageVideoWrapper = styled.div`
  position: relative;
  width: 32.2rem;
  margin-left: 1.8rem;
  cursor: pointer;
`

export const ExpandIcon = styled(Icon)`
  position: absolute;
  top: 2rem;
  right: 2rem;
  color: #fff;
`

export const ImageRegular = styled(CloudinaryImage)`
  padding-bottom: calc(458 / 322 * 100%);
`

export const ImageCropped = styled(CroppedImage)`
  padding-bottom: calc(458 / 322 * 100%);
`

export const Video = styled.video`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
`

export const MobileVideo = styled(Video)`
  ${minWidth.mobile`
    display: none;
  `}
`

export const DesktopVideo = styled(Video)`
  display: none;

  ${minWidth.mobile`
    display: block;
    opacity: 0;
  `}
`

const ArticleImage = styled(CloudinaryImage)`
  width: 24.7rem;
  height: 17.4rem;

  ${minWidth.mobile`
    width: 25.3rem;
    height: 17.8rem;
  `};
`
export const ArticleCategory = styled.span`
  font-size: 1.2rem;
  line-height: 1.17rem;
  font-weight: 500;
  text-transform: uppercase;
  color: ${({ theme }) => theme.component?.text || theme.colors.text.primary};

  ${maxWidth.tablet`
    font-size: 1.2rem;
    margin: 0.8rem 0 0.8rem 0;
  `}

  ${maxWidth.mobile`
    font-size: 1.1rem;
    margin: 0.5rem 0 0.5rem 0;
  `}
`

const CollaboratorName = styled.div`
  font-size: 1.2rem;
  font-weight: 500;
  line-height: 1.62rem;
  letter-spacing: 0.2rem;
  text-transform: uppercase;
  color: ${({ theme }) => theme.component?.text || theme.colors.text.primary};
  opacity: 0.6;
`

const ArticleTitleLink = styled(Link)`
  color: ${({ theme }) => theme.component?.text || theme.colors.text.primary};
  margin-top: 0.5rem;
  font-size: 1.69rem;
  line-height: 2.23rem;
`

const Author = styled.div`
  margin-top: 0.5rem;
  font-size: 1.1rem;
  text-transform: uppercase;
  color: ${({ theme }) => theme.component?.text || theme.colors.text.primary};
  opacity: 0.8;

  ${minWidth.mobile`
    margin-top: 1.8rem;
    font-size: 1.13rem;
    line-height: 1.62rem;
    letter-spacing: 0.1rem;
  `}
`
export const IconWrapper = styled.div`
  position: absolute;
  top: 1.6rem;
  right: 1.2rem;
  z-index: 2;
`

export const ArticleCard = ({ article = {}, pageType, tracking, trackingId }) => {
  if (!article?.id) {
    return null
  }
  const { id, category, subcategory, previewImage, titles, partner, sponsor, url, authors } =
    article

  const { trackRender, trackView, trackClick } = useTracking()
  const ref1 = useRef()
  const ref2 = useRef()

  const collaborator = partner || sponsor
  const trackId =
    trackingId || (partner ? "partner_article" : sponsor ? "sponsor_article" : `article-${id}`)
  const extraProps = {
    ...(pageType && { page_type: pageType }),
    click_title: titles.site || titles.body,
    click_url: `/articles/${url}`,
    article_id: id,
    ...(collaborator &&
      (partner ? { partner_id: collaborator.partnerId } : { sponsor_id: collaborator.sponsorId })),
    category: "Content Engagement",
    content_category: category,
    content_subcategory: subcategory,
  }

  if (tracking && tracking.includes("Render")) {
    trackRender(ref1, trackId, extraProps)
  }

  if (tracking && tracking.includes("View")) {
    trackView(ref1, trackId, extraProps)
  }

  if (tracking && tracking.includes("Click")) {
    trackClick(ref1, trackId, extraProps)
    trackClick(ref2, trackId, extraProps)
  }

  const author = authors && authors[0]

  return (
    <ArticleCardContainer>
      <Link ref={ref1} to={`/articles/${url}`} title={titles.site || titles.body} tabIndex={-1}>
        <ArticleImage
          baseUrl={JSON.parse(previewImage).path}
          images={[{ transforms: "c_fill,g_auto,w_254,h_173,q_auto,f_auto,fl_lossy" }]}
          lazy
        />
      </Link>
      <ArticleContent>
        {collaborator && (
          <CollaboratorName>
            {partner ? "paid content for" : "sponsored by"} {collaborator.name}
          </CollaboratorName>
        )}
        <ArticleCategory>{category}</ArticleCategory>

        <ArticleTitleLink
          ref={ref2}
          to={`/articles/${url}`}
          title={titles.site || titles.body}
          aria-label={titles.site || titles.body}
        >
          {titles.site || titles.body}
        </ArticleTitleLink>
        {author &&
          (author.url ? (
            <Author>
              <Link to={`/wc/${author.url}`} title={author.name} tabIndex={-1}>
                {author.name}
              </Link>
            </Author>
          ) : (
            <Author>{author.name}</Author>
          ))}
      </ArticleContent>
      <IconWrapper>
        <img src={ArticleIcon} alt="article-icon" />
      </IconWrapper>
    </ArticleCardContainer>
  )
}
